import React, {
  FC,
  MouseEventHandler,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { Flex, FlexProps } from 'antd';
import * as Icon from 'react-feather';
import styled from 'styled-components';
import {
  colorGray,
  fontSizeSM,
  fontWeightStrong,
} from '@atlas-frontend/design-tokens';
import { DividerHeaderButtons } from './dividerHeader/DividerHeaderButtons';
import {
  CollapsibleHeaderCount,
  CollapsibleHeaderCountProps,
} from './CollapsibleHeaderCount';

export interface CollapsibleDividerProps
  extends PropsWithChildren,
    CollapsibleHeaderCountProps {
  id: string;
  title: ReactNode;
  flex?: FlexProps;
  defaultExpanded?: boolean;
  expanded?: boolean;
  onToggle?: (expanded: boolean) => void;
  onAddButtonClick?: MouseEventHandler<HTMLButtonElement>;
  onEditButtonClick?: MouseEventHandler<HTMLButtonElement>;
  onCloneButtonClick?: MouseEventHandler<HTMLButtonElement>;
  onDeleteButtonClick?: MouseEventHandler<HTMLButtonElement>;
  onEditGraphClick?: MouseEventHandler<HTMLButtonElement>;
  editGraphLabel?: string;
  hasError?: boolean;
  disabled?: boolean;
  disabledLabel?: string;
  isLoading?: 'add' | 'manage' | 'clone' | 'delete';
}

export const CollapsibleHeader: FC<CollapsibleDividerProps> = (props) => {
  const { title, children, flex, defaultExpanded, expanded, onToggle } = props;

  const [isExpanded, setIsExpanded] = useState(
    typeof expanded === 'boolean' ? expanded : defaultExpanded || false,
  );

  useEffect(() => {
    if (typeof expanded === 'boolean') {
      setIsExpanded(expanded);
    }
  }, [expanded]);

  const ToggleIcon = isExpanded ? ExpandedIcon : CollapsedIcon;

  const setExpanded = useCallback(
    (expanded: boolean) => {
      setIsExpanded(expanded);
      onToggle?.(expanded);
    },
    [onToggle],
  );

  const handleToggle = useCallback(() => {
    setExpanded(!isExpanded);
  }, [setExpanded, isExpanded]);

  return (
    <Flex vertical gap="large" {...flex}>
      <DividerHeaderButtons {...props}>
        <Title align="center" gap="small" onClick={handleToggle}>
          <ToggleIcon />
          {title}
          <CollapsibleHeaderCount {...props} />
        </Title>
      </DividerHeaderButtons>
      {isExpanded && children}
    </Flex>
  );
};

const ElementWrapper = styled(Flex)`
  position: relative;
  z-index: 1;
  background-color: white;
  padding-inline: 16px;
  margin-inline: 20px;
`;

const Title = styled(ElementWrapper)`
  cursor: pointer;

  // The same as Divider (see AtlasThemeProvider.tsx)
  font-size: ${fontSizeSM}px;
  font-weight: ${fontWeightStrong};
  letter-spacing: 0.1em;
  text-transform: uppercase;
`;

export const ExpandedIcon = styled(Icon.ChevronDown)`
  color: ${colorGray[6]};
`;
export const CollapsedIcon = styled(Icon.ChevronRight)`
  color: ${colorGray[6]};
`;
